import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../../slices/modals";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { RejectRemoteRequestReasons } from "../../../utilities/constants";
import "./_index.scss";

const RejectRemoteRequestModal = ({ id, open, data, onReject }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get the rejection status from Redux store
  const { rejectRemoteRequestStatus } = useSelector((state) => state.remoteRequest);


  const handleClose = () => {
    dispatch(closeModal("RejectRemoteRequestModal"));
  };

  const initialValues = {
    reason: "",
    customReason: "",
  };

  const validationSchema = Yup.object({
    reason: Yup.string().required("Please select a reason for rejection"),
    customReason: Yup.string().when("reason", {
      is: "Other...",
      then: Yup.string()
        .required("Please provide a custom reason")
        .min(10, "Custom reason must be at least 10 characters"),
      otherwise: Yup.string(),
    }),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSubmitting(true);
    setSubmitting(true);

    try {
      const finalReason = values.reason === "Other..." 
        ? values.customReason 
        : values.reason;

      // Try both onReject prop and data.onReject
      const rejectFunction = onReject || data?.onReject;
      
      if (rejectFunction) {
        await rejectFunction(data?.id || id, finalReason);
        dispatch(closeModal("RejectRemoteRequestModal"));
      } else {
        console.error("onReject function is not available");
        setSubmitting(false);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error rejecting remote request:", error);
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  // Reset local state when modal closes or Redux state changes
  useEffect(() => {
    if (!open) {
      setIsSubmitting(false);
    }
  }, [open]);

  // Close modal when rejection is successful
  useEffect(() => {
    if (rejectRemoteRequestStatus === "succeeded") {
      dispatch(closeModal("RejectRemoteRequestModal"));
    }
  }, [rejectRemoteRequestStatus, dispatch]);


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="reject-remote-request-dialog-title"
      className="ta-modal reject-remote-request-modal"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="reject-remote-request-dialog-title" className="modal_header">
        <span>Reject Remote Work Request</span>
        <div className="close_button">
          <CloseIcon onClick={handleClose} />
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="reject-modal-content">
          <p className="reject-modal-description">
            Please provide a reason for rejecting this remote work request. This will be sent to the employee.
          </p>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ values, errors, touched, setFieldValue }) => (
              <Form className="reject-form">
                <div className="form-group">
                  <label htmlFor="reason">Reason for Rejection *</label>
                  <Field
                    as="select"
                    name="reason"
                    className={`form-control ${errors.reason && touched.reason ? "error" : ""}`}
                  >
                    <option value="">Select a reason...</option>
                    {RejectRemoteRequestReasons.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </Field>
                  {errors.reason && touched.reason && (
                    <div className="error-message">{errors.reason}</div>
                  )}
                </div>

                {values.reason === "Other..." && (
                  <div className="form-group">
                    <label htmlFor="customReason">Custom Reason *</label>
                    <Field
                      as="textarea"
                      name="customReason"
                      rows="4"
                      placeholder="Please provide a detailed reason for rejection..."
                      className={`form-control ${errors.customReason && touched.customReason ? "error" : ""}`}
                    />
                    <div className="character-count">
                      {values.customReason.length} characters
                    </div>
                    {errors.customReason && touched.customReason && (
                      <div className="error-message">{errors.customReason}</div>
                    )}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn btn-secondary"
                    disabled={isSubmitting || rejectRemoteRequestStatus === "loading"}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-danger"
                    disabled={isSubmitting || rejectRemoteRequestStatus === "loading"}
                  >
                    {isSubmitting || rejectRemoteRequestStatus === "loading" ? "Rejecting..." : "Reject Request"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RejectRemoteRequestModal;